import { useCallback, useMemo, useState } from 'react';
import type { Account } from '../types';

const PAGE_SIZE = 75;

export function useDashboardFilters(accounts: Account[]) {
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortBy, setSortBy] = useState<'name' | 'type'>('type');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = accounts;
    if (q) out = out.filter((a) => a.name.toLowerCase().includes(q) || a.number.includes(q));
    if (typeFilter !== 'All') out = out.filter((a) => a.type === typeFilter);
    out = [...out].sort((a, b) => {
      const av = sortBy === 'name' ? a.name : a.type;
      const bv = sortBy === 'name' ? b.name : b.type;
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return out;
  }, [accounts, query, typeFilter, sortBy, sortDir]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );
  const pageStart = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, filtered.length);

  const handleSort = useCallback((col: 'name' | 'type') => {
    setSortBy((prev) => {
      if (prev === col) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return col;
    });
  }, []);

  const handleQueryChange = useCallback((q: string) => {
    setQuery(q);
    setPage(1);
  }, []);

  const handleTypeFilterChange = useCallback((t: string) => {
    setTypeFilter(t);
    setPage(1);
  }, []);

  const nextPage = useCallback(
    () => setPage((p) => (p * PAGE_SIZE < filtered.length ? p + 1 : p)),
    [filtered.length]
  );
  const prevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);

  return {
    query,
    typeFilter,
    sortBy,
    sortDir,
    page,
    filtered,
    paged,
    pageStart,
    pageEnd,
    handleQueryChange,
    handleTypeFilterChange,
    handleSort,
    nextPage,
    prevPage,
  };
}